/**
 * Tracks, per proxied connection, whether THIS tab's open transaction died with
 * its leader — and what to do with each statement that follows.
 *
 * ── WHY THE PROXY NEEDS THIS AT ALL ───────────────────────────────────────────
 * A transaction is not a thing the leader knows about as a unit. drizzle issues
 * `begin`, the body, and `commit` as separate round trips, and every one of them
 * is routed to WHICHEVER tab currently leads. So when the leader dies with a
 * transaction open on behalf of this tab, two things are true at once:
 *
 *   1. The transaction is GONE. The old leader's engine held it uncommitted and
 *      was torn down with the tab; the new leader opens the file with no
 *      transaction active.
 *   2. This tab does not know that. drizzle's `transaction()` will keep issuing
 *      statements — the rest of the body if no statement happened to be in
 *      flight at the moment of handover, or `rollback` after the one that was.
 *
 * Without this tracker those statements go to the NEW leader and run in
 * autocommit, one by one: the body would be half-applied outside any
 * transaction, and `rollback` would fail with "no transaction is active" — an
 * error that would REPLACE the honest `LeaderChangedError` on drizzle's way
 * out (its `catch` awaits the rollback before rethrowing).
 *
 * ── WHAT IT DOES INSTEAD ──────────────────────────────────────────────────────
 * Transaction depth is derived from the statement stream (the same idea as
 * `classify-leader-request-resendability.ts`, but at CALL time rather than
 * dispatch time, because the decision must be made before the statement is
 * enqueued). Once a transaction is known to be orphaned:
 *
 *   - `rollback` / `rollback to savepoint` are answered locally as no-ops. There
 *     is nothing on the new leader to roll back, and letting drizzle's unwind
 *     succeed is what lets the ORIGINAL error escape `transaction()`.
 *   - Any other statement is refused with `LeaderChangedError` without being
 *     sent. Running it would silently break atomicity.
 *   - `begin` clears the orphaned state: the caller has moved on to a new
 *     transaction, and the new leader has no transaction open, so the two are
 *     in agreement again. (This is also what bounds the blast radius for a raw
 *     caller that never sends `rollback` — it is refused until its next
 *     `begin`, never for the life of the connection.)
 *
 * And the one classification that decides replay safety: a `LeaderChangedError`
 * on the `commit` itself is stamped NOT replayable (the commit may have landed
 * before the leader died), see `leader-transaction-replay.ts`.
 *
 * Pure — no browser APIs, no timers — so it is unit-testable in Jest, which is
 * where every decision that can corrupt data belongs.
 */
import { splitSqlStatements } from "@-/com.npmjs.expo-sqlite";
import { isLeaderChangedError } from "@-/dev.improve.haniverse.lib.leader-resource/errors";
import { classifyTransactionControl } from "#actor-sync-connection-lock";
import { markLeaderChangeNotReplayable } from "#leader-transaction-replay";

/**
 * `rollback to savepoint …` is deliberately NOT a top-level `rollback` for
 * `classifyTransactionControl` (it stays inside the held connection slot), but
 * for an orphaned transaction it is part of drizzle's unwind and must no-op
 * just the same.
 */
const ROLLBACK_TO_SAVEPOINT_RE = /^rollback\s+(?:transaction\s+)?to\s/;

export type OrphanedTransactionVerdict =
  /** Send it to the leader as usual. */
  | "dispatch"
  /** Part of unwinding a dead transaction: resolve locally, send nothing. */
  | "skip"
  /** Belongs to a dead transaction: refuse with `LeaderChangedError`, send nothing. */
  | "orphaned";

export interface OrphanedTransactionTracker {
  /**
   * Decide what to do with a statement that is ABOUT to be issued. Must be
   * called once per call, in call order, BEFORE `settle` for the same call.
   */
  classify(sql: string | null): OrphanedTransactionVerdict;
  /** Transaction depth as seen from this tab's statement stream (for tests). */
  readonly depth: number;
  /**
   * The leader — or its engine, in the pause case — went away. Any transaction
   * this tab had open is dead from this moment, whether or not a statement was
   * in flight.
   */
  markHandover(): void;
  /** Whether the current transaction is known to be dead (for tests). */
  readonly orphaned: boolean;
  /**
   * Record how a dispatched statement settled. `error === null` means it
   * resolved. May annotate `error` in place (the commit-not-replayable stamp).
   */
  settle(sql: string | null, error: unknown): void;
}

type TransactionControl = ReturnType<typeof classifyTransactionControl>;

function isRollbackToSavepoint(statement: string): boolean {
  return ROLLBACK_TO_SAVEPOINT_RE.test(statement.trim().toLowerCase());
}

/** Per-statement controls of a (possibly multi-statement) SQL source, in order. */
function controlsOf(sql: string | null): TransactionControl[] {
  if (sql === null) {
    return [];
  }
  return splitSqlStatements(sql).map((statement) => classifyTransactionControl(statement));
}

export function createOrphanedTransactionTracker(): OrphanedTransactionTracker {
  let depth = 0;
  let orphaned = false;

  function clear(): void {
    depth = 0;
    orphaned = false;
  }

  return {
    classify: (sql) => {
      // Non-SQL RPCs (`pull`, `push`, the wipe controls) are never part of a
      // transaction and must go through even while one is orphaned — the wipe
      // controls in particular are how the pause that orphaned it gets lifted.
      if (!orphaned || sql === null) {
        return "dispatch";
      }
      const [first] = controlsOf(sql);
      if (first === "begin") {
        // A new transaction: caller and new leader agree again (see header).
        clear();
        return "dispatch";
      }
      if (first === "rollback") {
        // The outer unwind. This is the last statement of the dead transaction.
        clear();
        return "skip";
      }
      if (
        sql !== null &&
        splitSqlStatements(sql).some((statement) => isRollbackToSavepoint(statement))
      ) {
        // A nested unwind; the outer `rollback` is still to come.
        return "skip";
      }
      return "orphaned";
    },
    get depth() {
      return depth;
    },
    markHandover: () => {
      if (depth > 0) {
        orphaned = true;
      }
    },
    get orphaned() {
      return orphaned;
    },
    settle: (sql, error) => {
      const controls = controlsOf(sql);
      if (error === null) {
        for (const control of controls) {
          if (control === "begin") {
            depth += 1;
          } else if (control === "commit" || control === "rollback") {
            depth = Math.max(0, depth - 1);
            if (depth === 0) {
              orphaned = false;
            }
          }
        }
        return;
      }
      if (isLeaderChangedError(error)) {
        // The whole engine that held this transaction is gone, so it is dead
        // whichever statement was lost: the `begin` (never took effect), a body
        // statement, or the `commit` — which is the one case where "dead" does
        // not mean "had no effect", hence the stamp.
        if (controls.includes("commit")) {
          markLeaderChangeNotReplayable(error);
        }
        if (depth > 0 || controls.includes("begin") || controls.includes("commit")) {
          orphaned = true;
        }
        if (controls.includes("rollback")) {
          // A lost `rollback` needs no unwind of its own: the transaction it was
          // unwinding died with the leader. Nothing further is expected from it.
          depth = Math.max(0, depth - 1);
          orphaned = depth > 0;
        }
        return;
      }
      // Any other failure leaves the leader-side transaction where it was — a
      // failed `commit` keeps the transaction open (drizzle rolls it back next),
      // and a failed `begin` never opened one. Only a failed `rollback` is
      // accounted for, in the same direction as success: whether or not the
      // engine agreed there was something to roll back, the caller is done.
      for (const control of controls) {
        if (control === "rollback") {
          depth = Math.max(0, depth - 1);
        }
      }
    },
  };
}
